
/**
 * Generated by AI Senior Engineer
 * Task: open browser
 * Analysis: General code generation task
 */

// TODO: Implement the following functionality:
// open browser

export function generatedFunction() {
    // Implementation will be added here
    console.log('This function was generated by AI Senior Engineer');
    // TODO: Add actual implementation
}

import { ModelConfig, ServiceStatus, APIClient } from '../types';

// Telebit tunnel URL for production/mobile access
const TELEBIT_URL = 'https://warm-mayfly-16.telebit.io';

// Dynamic API base - supports both Telebit tunnel and local development
const getApiBase = (): string => {
  // Check for runtime configuration (saved by user or Android bridge)
  const savedUrl = localStorage.getItem('omnigate_api_url');
  if (savedUrl) return savedUrl;

  // If running in Capacitor (Android/iOS app)
  if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.()) {
    return TELEBIT_URL;
  }

  // If accessed via Telebit tunnel, use it
  if (typeof window !== 'undefined' && window.location.hostname.includes('telebit.io')) {
    return window.location.origin;
  }

  // Production mode - use origin
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return window.location.origin;
  }

  // Development - use localhost backend on port 8888
  return 'http://localhost:8888';
};

const API_BASE = getApiBase();

// Dynamic key readers — always fetch from localStorage so post-login keys work immediately
const INVALID_PLACEHOLDER_KEYS = new Set(['admin-initial-key', 'admin-init-key', 'nexus-admin-2026', '']);
const getActiveKey = (): string => {
  const k = localStorage.getItem('omnigate_api_key') || '';
  return INVALID_PLACEHOLDER_KEYS.has(k) ? '' : k;
};
const getActiveAdminKey = (): string => {
  const k = localStorage.getItem('omnigate_admin_key') || '';
  return INVALID_PLACEHOLDER_KEYS.has(k) ? getActiveKey() : k;
};

const STORAGE_KEY_MODELS = 'omnigate_models';
const STORAGE_KEY_CLIENTS = 'omnigate_clients';

export class BackendService {
  private static instance: BackendService;
  private statsWs: WebSocket | null = null;
  private wsBase: string = this.getWebSocketBase();
  private isOfflineMode: boolean = false;

  private constructor() {
    this.checkBackendHealth();
  }

  public static getInstance(): BackendService {
    if (!BackendService.instance) {
      BackendService.instance = new BackendService();
    }
    return BackendService.instance;
  }

  private getWebSocketBase(): string {
    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Użyj backend URL zamiast hosta przeglądarki
      return `${protocol}//localhost:8888`;
    } catch {
      return '';
    }
  }

  private async checkBackendHealth() {
    try {
      const resp = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(2000) });
      this.isOfflineMode = !resp.ok;
    } catch {
      this.isOfflineMode = true;
    }
  }

  private getHeaders(): HeadersInit {
    const key = getActiveKey();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (key) {
      headers['Authorization'] = `Bearer ${key}`;
      headers['X-API-Key'] = key;
    }
    return headers;
  }

  private getAdminHeaders(): HeadersInit {
    const key = getActiveAdminKey();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (key) {
      headers['Authorization'] = `Bearer ${key}`;
      headers['X-API-Key'] = key;
      headers['X-Admin-Key'] = key;
    }
    return headers;
  }

  private mapAuthKeyToClient(key: any): APIClient {
    const rateLimit = Number(key?.rate_limit ?? 0);
    const rawActive = key?.is_active;
    const isActive = typeof rawActive === 'string'
      ? ['true', '1', 'yes'].includes(rawActive.toLowerCase())
      : Boolean(rawActive);
    const priority: APIClient['priority'] = rateLimit >= 1000
      ? 'HIGH'
      : rateLimit >= 250
        ? 'MEDIUM'
        : 'LOW';

    return {
      id: key?.id || `key-${Math.random().toString(36).slice(2, 8)}`,
      name: key?.name || 'Unnamed Key',
      apiKey: undefined,
      tokenBalance: 0,
      tokenLimit: rateLimit,
      usageCount: 0,
      lastActive: key?.last_used || 'Never',
      isActive,
      priority
    };
  }

  async executeMcpTool(server: string, tool: string, args: Record<string, any> = {}): Promise<any> {
      try {
          // V4 FIX: Ensure 'run_browser_agent' always receives a 'task' argument (only for this tool)
          if (tool === "run_browser_agent" && (!args.task || typeof args.task !== "string")) {
              // If task is missing, empty, or not a string, construct a meaningful task description
              args.task = typeof args === "string" ? args : (
                  args?.task || args?.url || args?.command || args?.text ||
                  "Przejdź na stronę internetową i wykonaj zadanie użytkownika"
              );
          }
          
          const resp = await fetch(`${API_BASE}/v1/omnigate/execute`, {
              method: 'POST',
              headers: this.getHeaders(),
              body: JSON.stringify({
                  server,
                  tool,
                  arguments: args
              })
          });
      if (!resp.ok) {
        throw new Error(`MCP execution failed: ${resp.status}`);
      }
      return await resp.json();
    } catch (err) {
      console.error('executeMcpTool error:', err);
      throw err;
    }
  }

  async fetchModels(): Promise<ModelConfig[]> {
    if (this.isOfflineMode) {
      const local = localStorage.getItem(STORAGE_KEY_MODELS);
      return local ? JSON.parse(local) : [];
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/models`, { headers: this.getHeaders() });
      if (!resp.ok) throw new Error();
      const data = await resp.json();
      const models = data.data || [];
      localStorage.setItem(STORAGE_KEY_MODELS, JSON.stringify(models));
      return models;
    } catch {
      this.isOfflineMode = true;
      return this.fetchModels();
    }
  }

  async fetchProviderModels(baseUrl: string, apiKey?: string): Promise<string[]> {
    const cleanUrl = baseUrl.replace(/\/$/, '');

    // BUG-08 FIX: Wyodrębnij czysty origin (protocol://host:port) żeby uniknąć
    // dołączania /models do pełnej ścieżki (np. /v1/audio/transcriptions/models)
    let baseOrigin: string;
    try {
      const url = new URL(cleanUrl);
      baseOrigin = `${url.protocol}//${url.host}`;
    } catch {
      // Fallback: usuń ścieżkę jeśli URL constructor zawiedzie
      baseOrigin = cleanUrl.replace(/\/v1.*$|\/api.*$/, '');
    }

    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;

    // Try 1: OpenAI-compatible /v1/models (faster-whisper-server, LM Studio, itp.)
    try {
      const resp = await fetch(`${baseOrigin}/v1/models`, { headers, signal: AbortSignal.timeout(4000) });
      if (resp.ok) {
        const data = await resp.json();
        if (data.data && Array.isArray(data.data)) return data.data.map((m: any) => m.id);
      }
    } catch (e) { }

    // Try 2: Ollama /api/tags
    try {
      const resp = await fetch(`${baseOrigin}/api/tags`, { signal: AbortSignal.timeout(4000) });
      if (resp.ok) {
        const data = await resp.json();
        if (data.models && Array.isArray(data.models)) return data.models.map((m: any) => m.name);
      }
    } catch (e) { }

    // Try 3: Bare /models (niektóre custom Docker serwery)
    try {
      const resp = await fetch(`${baseOrigin}/models`, { headers, signal: AbortSignal.timeout(2000) });
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data)) return data.map((m: any) => m.id || m.name || String(m));
        if (data.data && Array.isArray(data.data)) return data.data.map((m: any) => m.id);
        if (data.models && Array.isArray(data.models)) return data.models.map((m: any) => m.id || m.name);
      }
    } catch (e) { }

    // Try 4: OmniGate audio router /v1/audio/models (ASR/TTS discovery)
    try {
      const resp = await fetch(`${baseOrigin}/v1/audio/models`, { headers, signal: AbortSignal.timeout(3000) });
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data?.data)) return data.data.map((m: any) => m.id || m.name || String(m));
        if (Array.isArray(data?.models)) return data.models.map((m: any) => m.id || m.name || String(m));
      }
    } catch (e) { }

    // Try 5: Legacy audio models endpoint /audio/models
    try {
      const resp = await fetch(`${baseOrigin}/audio/models`, { headers, signal: AbortSignal.timeout(3000) });
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data?.data)) return data.data.map((m: any) => m.id || m.name || String(m));
        if (Array.isArray(data?.models)) return data.models.map((m: any) => m.id || m.name || String(m));
      }
    } catch (e) { }

    return [];
  }

  async addModel(model: Partial<ModelConfig>): Promise<ModelConfig | null> {
    const id = `NODE-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
    const newModel = { ...model, id, status: ServiceStatus.ONLINE } as ModelConfig;

    if (this.isOfflineMode) {
      const models = await this.fetchModels();
      const updated = [...models, newModel];
      localStorage.setItem(STORAGE_KEY_MODELS, JSON.stringify(updated));
      return newModel;
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/models`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(newModel)
      });
      return resp.ok ? await resp.json() : null;
    } catch {
      return null;
    }
  }

  async updateModel(id: string, updates: Partial<ModelConfig>): Promise<boolean> {
    if (this.isOfflineMode) {
      const models = await this.fetchModels();
      const updated = models.map(m => m.id === id ? { ...m, ...updates } : m);
      localStorage.setItem(STORAGE_KEY_MODELS, JSON.stringify(updated));
      return true;
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/models/${id}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(updates)
      });
      return resp.ok;
    } catch {
      return false;
    }
  }

  async deleteModel(id: string): Promise<boolean> {
    if (this.isOfflineMode) {
      const models = await this.fetchModels();
      const updated = models.filter(m => m.id !== id);
      localStorage.setItem(STORAGE_KEY_MODELS, JSON.stringify(updated));
      return true;
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/models/${id}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      });
      return resp.ok;
    } catch {
      return false;
    }
  }

  async testEndpoint(url: string): Promise<{ success: boolean; message: string }> {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 3000);
      const resp = await fetch(url, { method: 'GET', signal: controller.signal });
      clearTimeout(id);
      return { success: true, message: "Node reachable" };
    } catch (e: any) {
      return { success: false, message: e.message || "Endpoint Unreachable" };
    }
  }

  /**
   * REAL CREDENTIAL MANAGEMENT
   */
  async fetchClients(): Promise<APIClient[]> {
    if (!this.isOfflineMode) {
      try {
        const resp = await fetch(`${API_BASE}/v1/auth/keys`, {
          headers: this.getAdminHeaders(),
          signal: AbortSignal.timeout(2000)
        });
        if (resp.ok) {
          const data = await resp.json();
          const keys = data.keys || [];
          const clients = keys.map((k: any) => this.mapAuthKeyToClient(k));
          localStorage.setItem(STORAGE_KEY_CLIENTS, JSON.stringify(clients));
          return clients;
        }
      } catch {
        this.isOfflineMode = true;
      }
    }
    const local = localStorage.getItem(STORAGE_KEY_CLIENTS);
    return local ? JSON.parse(local) : [];
  }

  async addClient(clientData: Partial<APIClient>): Promise<APIClient | null> {
    const tokenLimit = clientData.tokenLimit || 50000;
    const permissions = clientData.priority === 'HIGH'
      ? ['admin', 'read', 'write']
      : ['read', 'write'];

    const fallbackClient: APIClient = {
      id: `cli_${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
      name: clientData.name || 'Unnamed Client',
      apiKey: `og_live_${Math.random().toString(36).substr(2, 24)}`,
      tokenBalance: 0,
      tokenLimit,
      usageCount: 0,
      lastActive: 'Never',
      isActive: true,
      priority: clientData.priority || 'MEDIUM'
    };

    if (this.isOfflineMode) {
      const clients = await this.fetchClients();
      const updated = [...clients, fallbackClient];
      localStorage.setItem(STORAGE_KEY_CLIENTS, JSON.stringify(updated));
      return fallbackClient;
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/auth/keys/create`, {
        method: 'POST',
        headers: this.getAdminHeaders(),
        body: JSON.stringify({
          name: clientData.name || 'Unnamed Client',
          permissions,
          rate_limit: tokenLimit,
          services: ['all']
        })
      });

      if (!resp.ok) return null;

      const data = await resp.json();
      return {
        id: data.key_id,
        name: clientData.name || 'Unnamed Client',
        apiKey: data.key,
        tokenBalance: 0,
        tokenLimit,
        usageCount: 0,
        lastActive: 'Never',
        isActive: true,
        priority: clientData.priority || 'MEDIUM'
      };
    } catch {
      return null;
    }
  }

  async updateClient(id: string, updates: Partial<APIClient>): Promise<boolean> {
    if (this.isOfflineMode) {
      const clients = await this.fetchClients();
      const updated = clients.map(c => c.id === id ? { ...c, ...updates } : c);
      localStorage.setItem(STORAGE_KEY_CLIENTS, JSON.stringify(updated));
      return true;
    }

    try {
      const payload: Record<string, any> = { key_id: id };
      if (typeof updates.name === 'string') payload.name = updates.name;
      if (typeof updates.tokenLimit === 'number') payload.rate_limit = updates.tokenLimit;
      if (typeof updates.isActive === 'boolean') payload.is_active = updates.isActive;
      if (updates.priority) {
        payload.permissions = updates.priority === 'HIGH'
          ? ['admin', 'read', 'write']
          : ['read', 'write'];
      }

      const resp = await fetch(`${API_BASE}/v1/auth/keys/update`, {
        method: 'POST',
        headers: this.getAdminHeaders(),
        body: JSON.stringify(payload)
      });
      return resp.ok;
    } catch {
      return false;
    }
  }

  async deleteClient(id: string): Promise<boolean> {
    if (this.isOfflineMode) {
      const clients = await this.fetchClients();
      const updated = clients.filter(c => c.id !== id);
      localStorage.setItem(STORAGE_KEY_CLIENTS, JSON.stringify(updated));
      return true;
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/auth/keys/revoke`, {
        method: 'POST',
        headers: this.getAdminHeaders(),
        body: JSON.stringify({ key_id: id })
      });
      return resp.ok;
    } catch {
      return false;
    }
  }

  async validateApiKey(apiKey: string, endpoint: string = '/v1/chat'): Promise<{
    valid: boolean;
    reason?: string;
    keyInfo?: any;
    error?: string;
  }> {
    if (this.isOfflineMode) {
      return { valid: false, reason: 'Offline mode active' };
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/auth/keys/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: apiKey, endpoint })
      });

      if (!resp.ok) {
        const details = await resp.text();
        return { valid: false, error: `Validation request failed (${resp.status}): ${details}` };
      }

      const data = await resp.json();
      return {
        valid: Boolean(data.valid),
        reason: data.reason,
        keyInfo: data.key_info
      };
    } catch (error) {
      return { valid: false, error: `Validation error: ${error}` };
    }
  }

  async getClientUsage(keyId: string): Promise<{
    success: boolean;
    usage?: any;
    error?: string;
  }> {
    if (this.isOfflineMode) {
      return { success: false, error: 'Offline mode active' };
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/auth/usage/${encodeURIComponent(keyId)}`, {
        headers: this.getAdminHeaders()
      });

      if (!resp.ok) {
        const details = await resp.text();
        return { success: false, error: `Usage request failed (${resp.status}): ${details}` };
      }

      const data = await resp.json();
      return { success: true, usage: data.usage };
    } catch (error) {
      return { success: false, error: `Usage error: ${error}` };
    }
  }

  async generateClientToken(keyId: string): Promise<{
    success: boolean;
    token?: string;
    error?: string;
  }> {
    if (this.isOfflineMode) {
      return { success: false, error: 'Offline mode active' };
    }

    try {
      const resp = await fetch(`${API_BASE}/v1/auth/token/generate?key_id=${encodeURIComponent(keyId)}`, {
        method: 'POST',
        headers: this.getAdminHeaders()
      });

      if (!resp.ok) {
        const details = await resp.text();
        return { success: false, error: `Token generation failed (${resp.status}): ${details}` };
      }

      const data = await resp.json();
      return { success: true, token: data.token };
    } catch (error) {
      return { success: false, error: `Token generation error: ${error}` };
    }
  }

  connectStats(onMessage: (stats: any) => void) {
    if (!this.wsBase || this.isOfflineMode) return;
    try {
      if (this.statsWs) this.statsWs.close();
      this.statsWs = new WebSocket(`${this.wsBase}/ws/dashboard`);
      this.statsWs.onmessage = (e) => onMessage(JSON.parse(e.data).data);
    } catch { }
  }

  // Watchdog Service Methods
  async getWatchdogStatus(): Promise<any> {
    try {
      const resp = await fetch(`${API_BASE}/v1/watchdog/status`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(5000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch (error) {
      throw new Error(`Failed to fetch watchdog status: ${error}`);
    }
  }

  async triggerWatchdogCheck(): Promise<any> {
    try {
      const resp = await fetch(`${API_BASE}/v1/watchdog/check`, {
        method: 'POST',
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(5000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch (error) {
      throw new Error(`Failed to trigger watchdog check: ${error}`);
    }
  }

  async startWatchdogMonitoring(): Promise<void> {
    try {
      const resp = await fetch(`${API_BASE}/v1/watchdog/start`, {
        method: 'POST',
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    } catch (error) {
      throw new Error(`Failed to start watchdog monitoring: ${error}`);
    }
  }

  async stopWatchdogMonitoring(): Promise<void> {
    try {
      const resp = await fetch(`${API_BASE}/v1/watchdog/stop`, {
        method: 'POST',
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    } catch (error) {
      throw new Error(`Failed to stop watchdog monitoring: ${error}`);
    }
  }

  // ==========================================
  // Omnigate MCP Endpoints
  // ==========================================

  async getOmnigateServers(): Promise<any[]> {
    try {
      const resp = await fetch(`${API_BASE}/v1/omnigate/servers`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(5000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      return data.servers || [];
    } catch (error) {
      console.error('Failed to fetch Omnigate servers:', error);
      return [];
    }
  }

  async getOmnigateTools(): Promise<any[]> {
    try {
      const resp = await fetch(`${API_BASE}/v1/omnigate/tools`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(5000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      return data.tools || [];
    } catch (error) {
      console.error('Failed to fetch Omnigate tools:', error);
      return [];
    }
  }

  async executeOmnigateTool(toolName: string, server: string, args: Record<string, any> = {}): Promise<any> {
      try {
          // V4 FIX: Ensure 'run_browser_agent' always receives a 'task' argument (only for this tool)
          if (toolName === "run_browser_agent" && (!args.task || typeof args.task !== "string")) {
              // If task is missing, empty, or not a string, construct a meaningful task description
              args.task = typeof args === "string" ? args : (
                  args?.task || args?.url || args?.command || args?.text ||
                  "Przejdź na stronę internetową i wykonaj zadanie użytkownika"
              );
          }
          
          const body = { tool: toolName, server, arguments: args };
      const resp = await fetch(`${API_BASE}/v1/omnigate/execute`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(120000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch (error) {
      throw new Error(`Failed to execute tool ${toolName}: ${error}`);
    }
  }

  // ==========================================
  // Resilience / Circuit Breaker Endpoints
  // ==========================================

  async getResilienceStats(): Promise<any> {
    try {
      const resp = await fetch(`${API_BASE}/v1/resilience/stats`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch (error) {
      console.error('Failed to fetch resilience stats:', error);
      return { circuits: {}, services: [] };
    }
  }

  async getCircuitStatus(service: string): Promise<any> {
    try {
      const resp = await fetch(`${API_BASE}/v1/resilience/circuits/${service}`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch (error) {
      throw new Error(`Failed to get circuit status: ${error}`);
    }
  }

  async resetCircuit(service: string): Promise<boolean> {
    try {
      const resp = await fetch(`${API_BASE}/v1/resilience/circuits/${service}/reset`, {
        method: 'POST',
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      return resp.ok;
    } catch (error) {
      console.error('Failed to reset circuit:', error);
      return false;
    }
  }

  // ==========================================
  // Tenant Management Endpoints
  // ==========================================

  async getCurrentTenant(): Promise<any> {
    try {
      const resp = await fetch(`${API_BASE}/v1/tenants/me`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch (error) {
      console.error('Failed to get current tenant:', error);
      return null;
    }
  }

  async getTenantUsage(): Promise<any> {
    try {
      const resp = await fetch(`${API_BASE}/v1/tenants/me/usage`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      return await resp.json();
    } catch (error) {
      console.error('Failed to get tenant usage:', error);
      return null;
    }
  }

  // ==========================================
  // System Metrics (CPU, RAM, Services)
  // ==========================================

  /**
   * Fetches real-time system metrics from the backend watchdog.
   * Returns CPU %, memory %, and service statuses for Dashboard charts.
   */
  async getSystemMetrics(): Promise<{
    cpu: number;
    memory: number;
    disk: number;
    services: { name: string; status: string; latency?: number }[];
    uptime: number;
    timestamp: string;
  }> {
    try {
      const resp = await fetch(`${API_BASE}/v1/watchdog/status`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(5000)
      });
      if (resp.ok) {
        const data = await resp.json();
        // Map watchdog status to metrics format
        const services = Object.entries(data.services || {}).map(([name, info]: [string, any]) => ({
          name,
          status: info.status || 'unknown',
          latency: info.latency_ms
        }));
        return {
          cpu: data.system?.cpu_percent ?? 0,
          memory: data.system?.memory_percent ?? 0,
          disk: data.system?.disk_percent ?? 0,
          services,
          uptime: data.uptime_seconds ?? 0,
          timestamp: new Date().toISOString()
        };
      }
    } catch (e) {
      // Fallback: try diagnostic health endpoint
    }

    // Fallback to diagnostic endpoint
    try {
      const resp = await fetch(`${API_BASE}/api/v1/diagnostic/health`, {
        headers: this.getHeaders(),
        signal: AbortSignal.timeout(3000)
      });
      if (resp.ok) {
        return {
          cpu: 0,
          memory: 0,
          disk: 0,
          services: [{ name: 'backend', status: 'healthy' }],
          uptime: 0,
          timestamp: new Date().toISOString()
        };
      }
    } catch (e) { }

    return {
      cpu: 0,
      memory: 0,
      disk: 0,
      services: [],
      uptime: 0,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Analyzes an image using the vision router.
   * Accepts a File object, data URL, or raw base64 string.
   * Sends JSON body with image_base64 field (NOT multipart) — matches /v1/vision/analyze.
   */
  async analyzeImage(imageData: File | string, prompt?: string): Promise<{ description: string; tags: string[] }> {
    try {
      let base64Data: string;

      if (imageData instanceof File) {
        // Convert File → base64 via FileReader
        base64Data = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Strip "data:image/...;base64," prefix if present
            resolve(result.includes(',') ? result.split(',')[1] : result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(imageData);
        });
      } else if (imageData.startsWith('data:')) {
        // data URL — strip prefix
        base64Data = imageData.split(',')[1];
      } else {
        // Already raw base64
        base64Data = imageData;
      }

      const resp = await fetch(`${API_BASE}/v1/vision/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('omnigate_api_key') || 'admin-initial-key'}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_base64: base64Data,
          prompt: prompt || 'Opisz kluczowe elementy obrazu bardzo krótko (max 2 zdania, bez rozumowania krok po kroku).',
          model: 'qwen3-vl:4b',
          response_max_tokens: 120,
          fast_mode: true
        }),
        signal: AbortSignal.timeout(12000)
      });

      if (resp.ok) {
        const data = await resp.json();
        return {
          description: data.analysis || data.description || 'Analysis complete',
          tags: data.tags || []
        };
      }
      throw new Error(`Vision API error: ${resp.status}`);
    } catch (error) {
      console.error('Vision analysis failed:', error);
      return { description: 'Analysis failed', tags: [] };
    }
  }

  // ==========================================
  // Configuration Methods
  // ==========================================

  setApiUrl(url: string): void {
    localStorage.setItem('omnigate_api_url', url);
    window.location.reload();
  }

  setApiKey(key: string): void {
    localStorage.setItem('omnigate_api_key', key);
  }

  getApiUrl(): string {
    return API_BASE;
  }
}

export const backendService = BackendService.getInstance();
